import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns'

/**
 * How urgent an order's due date is, from the point of view of an admin
 * scanning a list. Buckets are by *calendar* day, not elapsed hours: an order
 * due at 9am today and one due at 11pm today are both "today".
 */
export type DueBucket = 'unset' | 'overdue' | 'today' | 'soon' | 'upcoming'

/** Days out (inclusive) that still count as "soon" rather than "upcoming". */
export const SOON_WITHIN_DAYS = 3

export type DueDateInfo = {
  bucket: DueBucket
  /** Calendar days from today. Negative when overdue, null when unset. */
  daysOut: number | null
  /** Short text for the chip, e.g. "3d late", "Due today", "12 Aug". */
  label: string
  /** Longer text for the title attribute. */
  tooltip: string
  /** `yyyy-MM-dd`, ready for an <input type="date"> value. Empty when unset. */
  inputValue: string
}

/**
 * Surfaces are the bucket hue at low opacity over the current background, so a
 * chip keeps working in either theme without a second set of dark: classes.
 * The token definitions live in `globals.css`.
 */
const CLASSES: Record<DueBucket, string> = {
  unset: 'border-dashed border-input bg-background text-muted-foreground',
  overdue: 'border-due-overdue/25 bg-due-overdue/10 text-due-overdue',
  today: 'border-due-today/25 bg-due-today/10 text-due-today',
  soon: 'border-due-soon/30 bg-due-soon/10 text-due-soon',
  upcoming: 'border-due-upcoming/25 bg-due-upcoming/10 text-due-upcoming',
}

/**
 * Hover is a separate export so a read-only chip does not pick it up: a
 * surface that reacts to the cursor reads as clickable, and on a list the
 * chip is not.
 */
const HOVER_CLASSES: Record<DueBucket, string> = {
  unset: 'hover:bg-accent hover:text-accent-foreground',
  overdue: 'hover:bg-due-overdue/20',
  today: 'hover:bg-due-today/20',
  soon: 'hover:bg-due-soon/20',
  upcoming: 'hover:bg-due-upcoming/20',
}

/** Tailwind classes (border + background + text) for a bucket. */
export const dueDateClasses = (bucket: DueBucket): string => CLASSES[bucket]

/** Hover styles for interactive surfaces (the picker trigger). */
export const dueDateHoverClasses = (bucket: DueBucket): string => HOVER_CLASSES[bucket]

/**
 * The stored value may be a bare `yyyy-MM-dd` or a full timestamp, depending on
 * whether it came straight from the date input or back from the database.
 * `parseISO` handles both, but a bare date must not be shifted by the local
 * timezone -- so only the date portion is ever parsed.
 */
function parseDueDate(value?: string | null): Date | null {
  if (!value) return null
  const datePart = value.split('T')[0]!
  const parsed = parseISO(datePart)
  return isValid(parsed) ? parsed : null
}

export function getDueDateInfo(value?: string | null, today: Date = new Date()): DueDateInfo {
  const due = parseDueDate(value)

  if (!due) {
    return {
      bucket: 'unset',
      daysOut: null,
      label: 'Set due date',
      tooltip: 'No due date set',
      inputValue: '',
    }
  }

  const daysOut = differenceInCalendarDays(due, today)
  const pretty = format(due, 'dd MMM yyyy')
  const inputValue = format(due, 'yyyy-MM-dd')

  if (daysOut < 0) {
    const late = Math.abs(daysOut)
    return {
      bucket: 'overdue',
      daysOut,
      label: `${late}d late`,
      tooltip: `Was due ${pretty} — ${late} day${late === 1 ? '' : 's'} overdue`,
      inputValue,
    }
  }

  if (daysOut === 0) {
    return { bucket: 'today', daysOut, label: 'Due today', tooltip: `Due today, ${pretty}`, inputValue }
  }

  if (daysOut <= SOON_WITHIN_DAYS) {
    return {
      bucket: 'soon',
      daysOut,
      label: `Due in ${daysOut}d`,
      tooltip: `Due ${pretty} — in ${daysOut} day${daysOut === 1 ? '' : 's'}`,
      inputValue,
    }
  }

  return { bucket: 'upcoming', daysOut, label: pretty, tooltip: `Due ${pretty}`, inputValue }
}
